/**
 * What a run found, as four lines: held up · didn't survive · still open · next.
 *
 * The card at the end of a run in the exploration's feed (`ui/RunFound.tsx`).
 * `RunReport`'s header long refused a closing summary, because "a sentence
 * claiming more would be the tool declaring it had found something". This is
 * where that line is drawn, and it is drawn at the words, not at the card:
 *
 *  - Every line is a COUNT and the NAMES of claims whose own records say so —
 *    the readings the run's checks recorded, the endings it tallied as its nodes
 *    landed (`Run.endings`), the claims it added that are still open — each
 *    worded in the table the rest of the app words it in (`READING_LABEL`,
 *    `endingTally`, `ENDING_LABEL`), and each a press to the report section that
 *    lists it in full, under a title that is the line's own word.
 *  - Nothing is ranked, rated or praised. Names come in the order the run made
 *    its moves, or the section's own order; no line says "best", "most novel" or
 *    "key", and no line reads a model's self-rating. "Held up" is
 *    `core/heldUp.ts`'s definition — a reading from outside the model — and
 *    "didn't survive" keeps an ending in reasoning and a refutation by sources in
 *    their own words, so an objection that stood is never called "refuted" (D140).
 *
 * TWO TENSES. "Held up" and "didn't survive" are what the run found, in the past
 * tense, and come off its record as the account's counts do: a prune afterwards
 * does not un-find them. A check's reading is read through the verification its
 * own op wrote (by op id, from the claim's evidence history or the archive of
 * pruned claims), so a refutation the citation gate re-read on the way off disk
 * (invariant 2) counts as the gate now reads it; a re-check writes a new op, so
 * the join still finds this run's own check, and where it finds nothing the
 * reading the run copied at the time stands. "Still open" and "next" are present
 * tense and are read against the graph as it stands: a claim stops being open the
 * moment something is derived from it, and a line that kept counting it would
 * send the reader to a section where it no longer is. "Still open" counts the
 * claims the run's own passes added (`Op.produced`, the ledger's record) by the
 * report's rule (`stillOpenAs`), so its count is a part of the section it opens.
 * "Next" is what the run's own policy would take first if it were given more
 * budget (`chooseMove`, the function the loop calls, with its own reason), and
 * after a Stop, or an API that stopped answering (D337), the moves the run left;
 * the caller computes it — the policy is the engine's — and hands it in as data. It names a claim and presses
 * nothing.
 *
 * Pure, and data only: a line's destination is a descriptor, never a closure —
 * invariant 9's shape, although nothing here could spend — so the card is a
 * `.map()` over values a test can hold.
 *
 * A FIFTH LINE, FOR A RUN THAT CONJECTURED (D289). A run given conjecture passes
 * lands proposals nobody has searched for, owes each a prior-art search in a
 * check slot, and can end before it pays (D281) — and the four lines said none of
 * it, while the account did. So a run that ran a conjecture pass gets one more
 * line, in the same shape: how many it proposed and their names, in the run's
 * order, and how many of those are still not searched for prior art under the
 * words they carry now, pressing to the Overview's Insights fold (the reader's
 * word for a conjecture since D333), where the
 * "Not searched for prior art" filter lists them. No reading is named on it: a
 * search's reading is the card's to print with its note, and "not found" in a
 * line of counts would read as the one thing invariant 12 forbids. "Next" stays
 * the policy's pick — pointing it at an unsearched conjecture would put a second
 * rule under the one word D267 gave the plain press's first choice. Every other
 * run keeps four.
 */

import type { MoveReason, ReasonNode, Run, RunMove, Session, SourceReading } from './types'
import { MOVE_REASON, OP_PAST } from './types'
import {
  FOUND_CONJECTURES_WORD,
  FOUND_WORD,
  READING_LABEL,
  STATUS_WORD,
  endingCount,
  type FoundLine,
} from './vocabulary'
import { endingTally, runEndingsLine } from './endings'
import { readingHeldUp } from './heldUp'
import { STILL_OPEN_ORDER, stillOpenAs } from './stillOpen'
import { priorArtLive } from './priorArt'

/** The report sections a line opens, by the ids `reportDocument` gives them.
 *  One table, so a section renamed in the report cannot strand its line. */
export const FOUND_SECTION = {
  heldUp: 'held-up',
  didNotSurvive: 'did-not-survive',
  open: 'open',
} as const

/** The conjecture line's destination: the Overview's own Insights fold, the
 *  ledger — a live section of the tab, not one of `reportDocument`'s, so it is
 *  kept out of `FOUND_SECTION`, whose every id the report must carry. */
export const CONJECTURES_SECTION = 'conjectures'

export type FoundSection =
  (typeof FOUND_SECTION)[keyof typeof FOUND_SECTION] | typeof CONJECTURES_SECTION

/** Those sections' titles, which the report prints and the lines' presses name:
 *  each is its line's word, so "Held up" opens "What held up". */
export const FOUND_SECTION_TITLE: Record<FoundSection, string> = {
  'held-up': `What ${FOUND_WORD.heldUp.toLowerCase()}`,
  'did-not-survive': `What ${FOUND_WORD.didNotSurvive.toLowerCase()}`,
  open: FOUND_WORD.open,
  [CONJECTURES_SECTION]: FOUND_CONJECTURES_WORD,
}

/** Which line a row is: the four, and the conjecture line a run that ran a
 *  conjecture pass adds. */
export type FoundRowLine = FoundLine | 'conjectures'

/** A line's word, as the card prints it. */
export const foundWord = (line: FoundRowLine): string =>
  line === 'conjectures' ? FOUND_CONJECTURES_WORD : FOUND_WORD[line]

/** Where a line is read in full. */
export type FoundTo =
  { kind: 'section'; id: FoundSection } | { kind: 'account' } | { kind: 'node'; id: string }

/** One clause of a line, with the claims it names. */
export interface FoundPart {
  /** The clause's words; absent where the names are the clause. */
  text?: string
  /** Claims by label, in the run's order or the section's — after a colon, or
   *  straight on where the text runs into them (`into`). */
  names?: string[]
  into?: true
  /** How many more the clause counts than it names. */
  more?: number
}

export interface FoundRow {
  line: FoundRowLine
  /**
   * The line's count, or null where it has none to print. A nought reads as a
   * target missed rather than as a thing that did not happen — the account's
   * rule for its own counts — so a line at zero says what did not happen in words.
   */
  count: number | null
  parts: FoundPart[]
  /** Next's reason, in the policy's own sentence (`MOVE_REASON`). */
  why?: string
  to: FoundTo
}

/** What "Next" reads, handed in by the caller: the policy's first pick on the graph
 *  as it now stands, and after a Stop, or an API that stopped answering (D337),
 *  the moves the run left. */
export interface FoundNext {
  pick: { nodeId: string; label: string; why: MoveReason } | null
  left: number | null
}

/** How many claims a clause names before it counts the rest. Two, because a line
 *  is one line on a phone; the section it opens lists every one. */
export const FOUND_NAMED = 2

type Graph = Pick<Session, 'nodes' | 'order' | 'ops' | 'verificationArchive'>
/** What a check's own record is looked up in: the claim, and the archive of a pruned one. */
type Records = Pick<Session, 'nodes' | 'verificationArchive'>

const s = (n: number) => (n === 1 ? '' : 's')

/** The reading a check's own op wrote, wherever that record now lives. */
function readingOf(session: Records, m: RunMove): SourceReading | null {
  const n = session.nodes[m.nodeId]
  const rows = [
    ...(n?.verificationHistory ?? []),
    ...(n?.verification ? [n.verification] : []),
    ...(session.verificationArchive ?? [])
      .filter((row) => row.nodeId === m.nodeId)
      .map((row) => row.verification),
  ]
  return rows.find((v) => v.opId === m.opId)?.reading ?? null
}

/**
 * A reading the run copied, where the record it copied from cannot be found.
 *
 * The run keeps each check's reading as a bare word, so a pruned claim or a moved
 * record cannot rewrite its account — but a word carries none of the inputs
 * `refutedIsEarned` reads, so the gate cannot run on it. Invariant 2's rule for a
 * refutation the gate cannot earn is that it lands as `mixed`, and a copy nobody
 * can re-read is that case: printed as "sources state the opposite" it would be
 * the weaker second copy the invariant exists to stop, reachable by reloading.
 */
const unreread = (r: SourceReading): SourceReading => (r === 'refuted' ? 'mixed' : r)

/**
 * A run's source-check readings as the gate reads them now, in the order the run
 * recorded them: each joined to the record its own op wrote, re-gated with the
 * rest of the document on the way in; and where no record can be found, the copy,
 * with a refutation read as mixed. Every surface that prints a run's readings —
 * the account, the exports, the narrator's digest, this card — reads them here.
 */
export function runReadings(run: Run, session: Records): SourceReading[] {
  const landed = run.moves.filter((m) => m.kind === 'verify' && m.outcome === 'nodes')
  if (landed.length !== run.checkReadings.length) return run.checkReadings.map(unreread)
  return landed.map((m, k) => readingOf(session, m) ?? unreread(run.checkReadings[k]!))
}

/**
 * The run's checks that recorded a reading, each with the claim its move copied —
 * or null when the record cannot pair them.
 *
 * The loop pushes a reading exactly when a check lands and records that move's
 * outcome as `nodes`, so the k-th such move is the k-th reading. A record where
 * the two lists do not line up (a hand-edited document, a move the codec
 * dropped) is counted from its readings alone and names nothing, rather than
 * pairing a reading with the wrong claim.
 */
function checksOf(run: Run, session: Graph): { reading: SourceReading; label: string }[] | null {
  const landed = run.moves.filter((m) => m.kind === 'verify' && m.outcome === 'nodes')
  if (landed.length !== run.checkReadings.length) return null
  return landed.map((m, k) => ({
    reading: readingOf(session, m) ?? unreread(run.checkReadings[k]!),
    label: m.label,
  }))
}

/** The claims this run's own passes added that are still open, in the section's
 *  order: stated unknowns, then results that settled nothing, then untaken ends. */
function stillOpenOf(run: Run, session: Graph) {
  const passes = new Set(run.moves.map((m) => m.opId))
  const added = new Set(session.ops.filter((o) => passes.has(o.id)).flatMap((o) => o.produced))
  const derived = new Set<string>()
  for (const id of session.order) {
    const p = session.nodes[id]?.parentId
    if (p) derived.add(p)
  }
  const mine = session.order
    .filter((id) => added.has(id))
    .map((id) => session.nodes[id])
    .filter((n) => n !== undefined)
    .map((n) => ({ n, as: stillOpenAs(n, derived.has(n.id)) }))
  return STILL_OPEN_ORDER.flatMap((as) => mine.filter((x) => x.as === as))
}

/** The conjectures this run's own conjecture passes landed that are still on the
 *  graph and still conjectures, in the graph's order. */
function conjecturesOf(run: Run, session: Graph): ReasonNode[] {
  const passes = new Set(run.moves.filter((m) => m.kind === 'conjecture').map((m) => m.opId))
  const landed = new Set(session.ops.filter((o) => passes.has(o.id)).flatMap((o) => o.produced))
  return session.order
    .filter((id) => landed.has(id))
    .map((id) => session.nodes[id])
    .filter((n): n is ReasonNode => n?.kind === 'conjecture' && n.conjecture !== undefined)
}

/** Whether a prior-art search has read a conjecture under the words it carries
 *  now — the coach's `unsearched` rule and the run's own queue, read the same way. */
const searchedNow = (n: ReasonNode): boolean =>
  (n.conjecture?.priorArt ?? []).some((s) => priorArtLive(n, s))

const named = (labels: string[]): Pick<FoundPart, 'names' | 'more'> =>
  labels.length
    ? {
        names: labels.slice(0, FOUND_NAMED),
        ...(labels.length > FOUND_NAMED ? { more: labels.length - FOUND_NAMED } : {}),
      }
    : {}

/**
 * The card's lines for one finished run. `next` is null for a run whose Next is
 * not the reader's to act on — any run but the newest, or one that ran the
 * agenda, whose next step is the queue's — and the card is then three lines.
 */
export function runFound(run: Run, session: Graph, next: FoundNext | null): FoundRow[] {
  const checks = checksOf(run, session)
  const readings = checks ? checks.map((c) => c.reading) : runReadings(run, session)
  const labelsWhere = (p: (r: SourceReading) => boolean) =>
    checks ? checks.filter((c) => p(c.reading)).map((c) => c.label) : []
  const rows: FoundRow[] = []

  /* ── held up: this run's checks whose sources appear to agree ── */
  {
    const held = readings.filter(readingHeldUp).length
    const checkMoves = run.moves.filter((m) => m.kind === 'verify').length
    rows.push({
      line: 'heldUp',
      count: held || null,
      parts: [
        held
          ? { text: READING_LABEL.supported.toLowerCase(), ...named(labelsWhere(readingHeldUp)) }
          : {
              // The account's warn sentences, shortened: nothing held up is a fact
              // about what was put in front of a source, and which fact it is
              // matters.
              text: readings.length
                ? `none of its ${readings.length} source check${s(readings.length)} read “${READING_LABEL.supported.toLowerCase()}”`
                : checkMoves
                  ? 'no source check it ran recorded a reading'
                  : 'nothing it added was put in front of a source',
            },
      ],
      to: { kind: 'section', id: FOUND_SECTION.heldUp },
    })
  }

  /* ── didn't survive: refuted by sources, and closed in reasoning ── */
  {
    const isRefuted = (r: SourceReading) => r === 'refuted'
    const refuted = readings.filter(isRefuted).length
    // Split only where the run's tally adds up to its own total (`runEndingsLine`'s
    // rule): a run stored before the endings were counted apart says the total in
    // the words true of every ending, and counts none of it here, because some of
    // it may be questions that are still open.
    const e = run.endings
    const split =
      !!e && (e['objection-stood'] ?? 0) + (e['path-failed'] ?? 0) + (e.open ?? 0) === run.dead
    const closed = split
      ? { 'objection-stood': e!['objection-stood'] ?? 0, 'path-failed': e!['path-failed'] ?? 0 }
      : {}
    const closedN = (closed['objection-stood'] ?? 0) + (closed['path-failed'] ?? 0)
    const parts: FoundPart[] = []
    if (refuted) {
      parts.push({ text: READING_LABEL.refuted.toLowerCase(), ...named(labelsWhere(isRefuted)) })
    }
    if (closedN) parts.push({ text: `${endingTally(closed)}, in reasoning` })
    if (!split && run.dead > 0) parts.push({ text: runEndingsLine(run)! })
    if (!parts.length) {
      parts.push({ text: 'nothing it added was closed in reasoning or refuted by sources' })
    }
    rows.push({
      line: 'didNotSurvive',
      count: refuted + closedN || null,
      parts,
      to: { kind: 'section', id: FOUND_SECTION.didNotSurvive },
    })
  }

  /* ── still open: what it added that nothing has settled or taken further ── */
  {
    const open = stillOpenOf(run, session)
    // The one open ending said in its own words, because it is the one that waits
    // on the reader rather than on another pass.
    const unsettleable = open.filter((x) => x.as === 'gap' && x.n.terminal).length
    rows.push({
      line: 'open',
      count: open.length || null,
      parts: open.length
        ? [
            named(open.map((x) => x.n.label)),
            ...(unsettleable ? [{ text: endingCount('open', unsettleable) }] : []),
          ]
        : [{ text: 'nothing it added is left open' }],
      to: { kind: 'section', id: FOUND_SECTION.open },
    })
  }

  /* ── conjectures: what its conjecture passes proposed, and what is still owed
     a search. Past tense for the count, off the run's own moves, as held up is;
     present tense for the search, against the graph, as still open is. ── */
  {
    const passes = run.moves.filter((m) => m.kind === 'conjecture')
    if (passes.length) {
      const proposed = passes.reduce((a, m) => a + m.produced, 0)
      const mine = conjecturesOf(run, session)
      const unsearched = mine.filter((n) => !searchedNow(n)).length
      const parts: FoundPart[] = [
        proposed
          ? { text: 'proposed', ...named(mine.map((n) => n.label)) }
          : {
              // D281's words: an empty conjecture pass is an answer, not a stall.
              text: `${STATUS_WORD.noConjectureLanded.toLowerCase()} in ${passes.length === 1 ? 'its insight pass' : `its ${passes.length} insight passes`}`,
            },
      ]
      if (mine.length) {
        parts.push({
          text: unsearched
            ? `${unsearched} ${STATUS_WORD.notSearchedForPriorArt.toLowerCase()}`
            : `${mine.length === 1 ? '' : 'each '}${OP_PAST.priorArt.toLowerCase()}`,
        })
      }
      rows.push({
        line: 'conjectures',
        count: proposed || null,
        parts,
        // The fold holds the run's conjectures only while one of them does; with
        // every one gone, the account is where its passes are still counted.
        to: mine.length ? { kind: 'section', id: CONJECTURES_SECTION } : { kind: 'account' },
      })
    }
  }

  /* ── next: what the run would do with more budget ── */
  if (next) {
    const parts: FoundPart[] = []
    if (next.left) parts.push({ text: `${next.left} move${s(next.left)} left` })
    parts.push(
      next.pick
        ? { text: 'a run would start on', names: [next.pick.label], into: true }
        : { text: 'no open end left that a run would take' },
    )
    rows.push({
      line: 'next',
      count: null,
      parts,
      ...(next.pick ? { why: MOVE_REASON[next.pick.why] } : {}),
      to: next.pick ? { kind: 'node', id: next.pick.nodeId } : { kind: 'account' },
    })
  }

  return rows
}
